/* global google */

// Delay before the post-fit nudge fires. The nudge must happen AFTER the
// DeckOverlay has attached (its internal poll maxes out at ~600ms) and
// AFTER Google Maps has settled the fitBounds viewport animation, otherwise
// the panBy is absorbed by the in-flight viewport change and no 'idle' event
// lands on the overlay. Tunable via the fourth arg if a caller needs to
// override, but the default is the safe value.
const DEFAULT_NUDGE_DELAY_MS = 700

/**
 * Runs fitFn once the map projection is ready, with a tiny 1-pixel panBy nudge
 * afterwards to guarantee the WebGL overlay (deck.gl) picks up the viewport
 * and draws its layers.
 *
 * Why the nudge: if fitBounds ends at the same viewport the map already has,
 * Google Maps doesn't fire an 'idle' event, and the DeckOverlay never calls
 * forceDraw — so markers/radius stay invisible until the user interacts.
 * A 1-pixel panBy is a harmless, imperceptible bump that always produces an
 * 'idle' event. Crucially it is NOT a padding change — padding nudges
 * visibly shift the viewport and look bad.
 *
 * Returns a cleanup fn that cancels pending listeners/timers.
 */
export const fitWhenMapReady = (map, fitFn, { nudgeDelayMs = DEFAULT_NUDGE_DELAY_MS } = {}) => {
  if (!map || typeof fitFn !== 'function') return () => {}

  let cancelled = false
  let idleListener = null
  let nudgeTimer = null

  const nudge = () => {
    if (cancelled || !map) return
    try {
      map.panBy(0, 1)
    } catch (_) {
      // no-op
    }
  }

  const run = () => {
    if (cancelled) return
    if (!map.getBounds?.()) {
      idleListener = map.addListener('idle', () => {
        if (idleListener) google.maps.event.removeListener(idleListener)
        idleListener = null
        run()
      })
      return
    }
    fitFn()
    // Delayed nudge — see DEFAULT_NUDGE_DELAY_MS comment for why it's not
    // immediate. Runs AFTER deck.gl has likely attached and fitBounds has
    // settled, so the panBy produces a fresh 'idle' that wakes the overlay.
    nudgeTimer = setTimeout(nudge, nudgeDelayMs)
  }

  run()

  return () => {
    cancelled = true
    if (idleListener) google.maps.event.removeListener(idleListener)
    if (nudgeTimer) clearTimeout(nudgeTimer)
  }
}
